<script setup lang="ts">
import { useTemplateRefsList, useIntervalFn, useScroll } from '@vueuse/core';
import type { SpacingProp } from '@/types/Layout';

const DEFAULT_ITEMS_PER_ROW = 'auto';
const DEFAULT_SPACE_BETWEEN = 16;

type SliderFade =
  | 'off'
  | 'both'
  | 'left'
  | 'right'
  | {
      both?: boolean;
      left?: boolean;
      right?: boolean;
    };

type Display = {
  itemsPerRow?: SpacingProp;
  spaceBetweenItems?: SpacingProp;
};

interface AutoplayOptions {
  delay?: number;
  direction?: 'left' | 'right';
}

type Props = {
  items?: any[];
  listClass?: string;
  replaceListStyle?: boolean;
  itemClass?: string;
  replaceItemStyle?: boolean;
  display?: Display;
  sliderFade?: SliderFade;
  enableNavigationButtons?: boolean;
  navigationAlwaysVisible?: boolean;
  autoplay?: AutoplayOptions;
};

const {
  items,
  listClass,
  replaceListStyle,
  itemClass,
  replaceItemStyle,
  display = {},
  sliderFade = 'off',
  enableNavigationButtons,
  navigationAlwaysVisible = false,
  autoplay = {
    delay: 0,
    direction: 'left',
  },
} = defineProps<Props>();

const listElementDiv = ref<HTMLDivElement | null>(null);
const itemsElementsDiv = useTemplateRefsList<HTMLDivElement>();

function getNextItemIndex(currentIndex: number) {
  if (currentIndex < activeIndex.value) {
    return lastIndex.value + 1 - activeIndex.value + currentIndex;
  }

  return currentIndex - activeIndex.value;
}

function getNextActiveIndex(): number {
  if (activeIndex.value >= lastIndex.value) {
    return 0;
  }

  return activeIndex.value + 1;
}

function getPrevActiveIndex(): number {
  if (activeIndex.value <= 0) {
    return lastIndex.value;
  }

  return activeIndex.value - 1;
}

function reorderItems() {
  listElementDiv.value?.classList.remove($style.transition);

  for (let i = 0; i <= lastIndex.value; i++) {
    itemsElementsDiv.value[i]!.style.order = String(getNextItemIndex(i) + 1);
  }

  return setTimeout(() => {
    return listElementDiv.value?.classList.add($style.transition);
  }, 50);
}

const { resume: startAutoplay } = useIntervalFn(
  () => {
    initAutoplay(autoplay);
  },
  autoplay.delay,
  { immediate: false },
);

const {
  x: horizontalScrollPosition,
  arrivedState,
  measure: updateListElementScrollState,
} = useScroll(listElementDiv, { behavior: 'smooth' });

const { left: isNavigationLeftArrived, right: isNavigationRightArrived } =
  toRefs(arrivedState);

const { getSpacingMeasurementFromPropList } = useLayout();

const $style = useCssModule();

const activeIndex = defineModel<number>('activeIndex', {
  default: 0,
});

const isNavigationLeftButtonDisabled = computed<boolean>(() => {
  return isNavigationLeftArrived.value || horizontalScrollPosition.value < 0;
});

const isNavigationRightButtonDisabled = computed<boolean>(() => {
  return isNavigationRightArrived.value;
});

const isSliderFadeLeftVisible = computed<boolean>(() => {
  if (isNavigationLeftButtonDisabled.value) {
    return false;
  }

  if (typeof sliderFade === 'string') {
    return sliderFade === 'left' || sliderFade === 'both';
  }

  return !!sliderFade?.left || !!sliderFade?.both;
});

const isSliderFadeRightVisible = computed<boolean>(() => {
  if (isNavigationRightButtonDisabled.value) {
    return false;
  }

  if (typeof sliderFade === 'string') {
    return sliderFade === 'right' || sliderFade === 'both';
  }

  return !!sliderFade?.right || !!sliderFade?.both;
});

const isSliderFadeVisible = computed<boolean>(() => {
  return isSliderFadeLeftVisible.value || isSliderFadeRightVisible.value;
});

const isNavigationButtonsVisible = computed<boolean>(() => {
  return !!enableNavigationButtons;
});

const itemsPerRow = computed<number>(() => {
  return getSpacingMeasurementFromPropList(
    display.itemsPerRow || DEFAULT_ITEMS_PER_ROW,
  );
});

const spaceBetweenItems = computed<number>(() => {
  return getSpacingMeasurementFromPropList(
    display.spaceBetweenItems || DEFAULT_SPACE_BETWEEN,
  );
});

const spaceBetweenItemsInPixels = computed<string>(() => {
  return spaceBetweenItems.value + 'px';
});

const itemFlexBasisCSS = computed<string>(() => {
  if (itemsPerRow.value === 0) {
    return 'auto';
  }

  const sumTotalDistance = spaceBetweenItems.value * (itemsPerRow.value - 1);

  return `calc((100% - ${sumTotalDistance}px) / ${itemsPerRow.value})`;
});

const itemFlexShrinkCSS = computed<string>(() => {
  if (itemFlexBasisCSS.value === 'auto') {
    return '1';
  }

  return '0';
});

const itemWidth = ref<number>(0);

const itemWidthCSS = computed<string>(() => {
  if (itemFlexBasisCSS.value !== 'auto') {
    return itemFlexBasisCSS.value;
  }

  return `${itemWidth.value || 0}px`;
});

const $list = computed<string>(() => {
  if (!listClass) {
    return $style.list;
  }

  if (replaceListStyle) {
    return listClass;
  }

  return `${$style.list} ${listClass}`;
});

const $item = computed<string>(() => {
  if (!itemClass) {
    return $style.item;
  }

  if (replaceItemStyle) {
    return itemClass;
  }

  return `${$style.item} ${itemClass}`;
});

function scrollToLeft(distance: number = 300) {
  horizontalScrollPosition.value -= distance;
}

function scrollToRight(distance: number = 300) {
  horizontalScrollPosition.value += distance;
}

const lastIndex = computed<number>(() => {
  return itemsElementsDiv.value.length - 1;
});

function initAutoplay(options: AutoplayOptions = {}) {
  const { direction = 'left' } = options;

  if (direction === 'left') {
    activeIndex.value = getNextActiveIndex();
  }

  if (direction === 'right') {
    activeIndex.value = getPrevActiveIndex();
  }

  if (itemFlexBasisCSS.value === 'auto') {
    const currentElement = itemsElementsDiv.value[activeIndex.value];

    itemWidth.value = currentElement?.getBoundingClientRect()?.width || 0;
  }

  reorderItems();
}

const isAutoPlayActive = computed(() => {
  return !!autoplay.delay;
});

onMounted(() => {
  nextTick(() => {
    updateListElementScrollState();
  });

  if (isAutoPlayActive.value) {
    startAutoplay();
  }
});

watchEffect(() => {
  if (isNaN(activeIndex.value)) {
    return;
  }

  reorderItems();
});

defineExpose({
  scrollToLeft,
  scrollToRight,
  isNavigationLeftButtonDisabled,
  isNavigationRightButtonDisabled,
});
</script>

<template>
  <div
    :class="[
      $style.carousel,
      {
        [$style.sliderFade]: isSliderFadeVisible,
        [$style.left]: isSliderFadeLeftVisible,
        [$style.right]: isSliderFadeRightVisible,
        [$style.navigationAlwaysVisible]: navigationAlwaysVisible,
      },
    ]"
  >
    <div
      ref="listElementDiv"
      :class="[
        $list,
        $style.noScrollbar,
        {
          [$style.autoplay]: isAutoPlayActive,
        },
      ]"
    >
      <slot name="items">
        <template
          v-for="(item, index) in items"
          :key="index"
        >
          <div
            :ref="itemsElementsDiv.set"
            :class="$item"
          >
            <slot
              name="item"
              v-bind="{ item, index }"
            />
          </div>
        </template>
      </slot>
    </div>

    <template v-if="isNavigationButtonsVisible">
      <slot
        name="navigation-to-left"
        v-bind="{ scrollToLeft, isDisabled: isNavigationLeftButtonDisabled }"
      >
        <button
          v-if="!isNavigationLeftButtonDisabled"
          :class="[$style.navigationButton, $style.left]"
          @click="scrollToLeft()"
        >
          <NuxtIcon name="arrows/angle_left" />
        </button>
      </slot>

      <slot
        name="navigation-to-right"
        v-bind="{ scrollToRight, isDisabled: isNavigationRightButtonDisabled }"
      >
        <button
          v-if="!isNavigationRightButtonDisabled"
          :class="[$style.navigationButton, $style.right]"
          @click="scrollToRight()"
        >
          <NuxtIcon name="arrows/angle_right" />
        </button>
      </slot>
    </template>
  </div>
</template>

<style module lang="postcss">
.carousel {
  @apply relative w-full;
}

.list {
  @apply flex relative z-0 overflow-x-auto scroll-smooth overflow-y-hidden;

  &.autoplay {
    @apply overflow-visible;

    left: calc(v-bind(itemWidthCSS) * -1);
    transform: translateX(v-bind(itemWidthCSS));
  }

  &:not(.autoplay) {
    @apply snap-mandatory snap-x;
  }

  &.transition {
    transform: none;
    transition: all 0.5s cubic-bezier(0.23, 1, 0.32, 1) !important;
  }

  gap: v-bind(spaceBetweenItemsInPixels);
}

.item {
  @apply w-full select-none;
  /* @apply snap-start; */

  flex-basis: v-bind(itemFlexBasisCSS);
  flex-shrink: v-bind(itemFlexShrinkCSS);
}

.sliderFade {
  &.left {
    &:before {
      /* Fade removido */
    }
  }

  &.right {
    &:after {
      /* Fade removido */
    }
  }
}

.navigationButton {
  @apply w-8 h-full grid place-items-center z-20 text-texts absolute top-0 invisible;

  &.left {
    @apply left-0;
  }

  &.right {
    @apply right-0;
  }
}

.noScrollbar {
  -ms-overflow-style: none !important;
  scrollbar-width: none !important;

  &::-webkit-scrollbar {
    display: none !important;
  }
}

.carousel:hover,
.carousel.navigationAlwaysVisible {
  .navigationButton {
    @apply visible;
  }
}

/* Mobile: faz o carousel encostar na lateral direita */
@media (max-width: 414px) {
  .list {
    margin-right: calc(-1 * var(--container-padding, 16px));
    padding-right: var(--container-padding, 16px);
  }
}
</style>
